import { X } from "lucide-react";
import { AvatarBubble } from "../../../components/ui/voter-avatars";
import { getUserLabel } from "../../../lib/identity";

/**
 * Phase 4b — Active overlay chips above the Create/Edit Poll calendar.
 *
 * Each chip = `[avatar] [name] [✕]`, with an outer color glow keyed to
 * the friend's resolved overlay color (matches the chip color shown in
 * the calendar event border). Clicking ✕ drops the overlay (does NOT
 * revoke the underlying share — owner controls revocation from the
 * Friends & Groups page). Empty `owners` renders nothing.
 *
 * The "self" overlay (when "Show my availability" is on) renders with
 * NO color glow so the user can immediately tell which chip is theirs.
 *
 * Props:
 *   owners        : Array<{ ownerId, displayName, avatarUrl, email?, isSelf? }>
 *   onRemove      : (ownerId: string) => void
 *   colorByOwnerId: map<ownerId, hex> — resolved color for each non-self
 *                   owner. Self chips skip the glow regardless.
 */
export function AvailabilityOverlayStrip({
  owners = [],
  onRemove,
  colorByOwnerId = {},
}) {
  if (!owners.length) return null;

  return (
    <div
      data-testid="availability-overlay-strip"
      className="flex flex-wrap items-center gap-2 rounded-xl border border-slate-200 bg-slate-50/60 px-3 py-2 dark:border-slate-700 dark:bg-slate-800/40"
    >
      <span className="text-xs font-medium uppercase tracking-wide text-slate-500 dark:text-slate-400">
        Overlays
      </span>
      {owners.map((owner) => {
        const isSelf = owner.isSelf === true;
        // Privacy: render the owner's CHOSEN public identifier — never their
        // raw email (which rides along on the object). getUserLabel returns
        // the email ONLY for email-type users; "You" for self.
        const name = isSelf ? "You" : getUserLabel(owner) || "Friend";
        const color = isSelf ? null : colorByOwnerId?.[owner.ownerId] || null;
        // Glow = soft outer shadow (8px) + tinted border. Self has neither.
        const style = color
          ? {
              borderColor: color,
              boxShadow: `0 0 0 1px ${color}, 0 0 8px 0 ${color}55`,
            }
          : undefined;
        return (
          <span
            key={owner.ownerId}
            data-testid={`overlay-chip-${owner.ownerId}`}
            data-overlay-self={isSelf ? "true" : undefined}
            data-overlay-color={color || undefined}
            style={style}
            className="inline-flex items-center gap-1.5 rounded-full border border-slate-200 bg-white px-2 py-1 text-xs text-slate-700 shadow-sm dark:border-slate-700 dark:bg-slate-900 dark:text-slate-200"
          >
            <AvatarBubble
              user={{
                displayName: owner.displayName,
                email: owner.email,
                photoURL: owner.avatarUrl,
                publicIdentifier: owner.publicIdentifier,
                publicIdentifierType: owner.publicIdentifierType,
                qsUsername: owner.qsUsername,
                discordUsername: owner.discordUsername,
              }}
              label={name}
              size={18}
            />
            <span className="max-w-[10rem] truncate font-medium">{name}</span>
            <button
              type="button"
              onClick={() => onRemove?.(owner.ownerId)}
              className="ml-0.5 rounded-full p-0.5 text-slate-400 transition-colors hover:bg-slate-100 hover:text-slate-700 dark:hover:bg-slate-700 dark:hover:text-slate-200"
              aria-label={
                isSelf
                  ? "Remove your availability overlay"
                  : `Remove ${getUserLabel(owner) || "overlay"}`
              }
              data-testid={`overlay-chip-remove-${owner.ownerId}`}
            >
              <X className="h-3 w-3" />
            </button>
          </span>
        );
      })}
    </div>
  );
}
